import React, { useCallback, useContext, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";

import { StateContext } from "../../../../components/context/StateProvider";
import Toasts from "../../../../components/notificacion/Toasts";
import { BADGE_COLORS } from "../../../../components/ui/badgeColors";
import { UsuarioForm } from "../forms";
import { useUsuarioFormData } from "../../hooks";
import SeccionConflictoCreacion from "./SeccionConflictoCreacion";

const MENSAJE_ERROR_CREAR = "No se pudo crear el usuario. Inténtalo de nuevo.";
const MENSAJE_ERROR_EDITAR = "No se pudo actualizar el usuario. Inténtalo de nuevo.";
const MENSAJE_ERROR_RESTAURAR = "No se pudo restaurar el usuario. Inténtalo de nuevo.";
const MENSAJE_CONFLICTO_BAJA = "Ese usuario ya existe pero está dado de baja.";
const MENSAJE_CONFLICTO_EXISTENTE = "Ya existe un usuario activo con ese nombre de usuario o email.";

/**
 * Modal responsive de alta y edición de usuario. Envuelve UsuarioForm en un
 * Dialog; el modo (alta/edición) se deriva de si recibe `usuario` por props.
 *
 * DECISION (props crearUsuario/actualizarUsuario en vez de useUsuarios()
 * propio): recibe ambas funciones desde la única instancia de useUsuarios que
 * vive en ListaUsuarios, en lugar de llamar al hook aquí dentro. Una segunda
 * instancia tendría su propio estado de lista independiente y su recarga tras
 * el alta/edición no refrescaría el DataGrid de la página. El catálogo de
 * roles (useUsuarioFormData) sí se resuelve en este modal: es de solo
 * lectura y no comparte estado con la lista, así que no hay ese riesgo.
 *
 * DECISION (toasts): igual que ModalDarDeBaja/ModalRestaurarUsuario (mismo
 * feature), el modal gestiona su propio loading local y su propio toast de
 * error vía StateContext, sin exigir que la página añada ese cableado.
 *
 * DECISION (no arrastrar datos del usuario anterior al reabrir): el contenido
 * del Dialog (UsuarioForm) solo se renderiza mientras `show` es true, y lleva
 * `key={usuario?.id ?? "nuevo"}`. Con `show=false` React desmonta el
 * formulario (no solo lo oculta), y la key fuerza además un remount si se
 * pasara de editar un usuario a otro sin cerrar de por medio. Al reabrirse,
 * UsuarioForm se instancia desde cero: su propio efecto de precarga corre en
 * el primer render, así que nunca conserva valores ni campos "tocados" de la
 * apertura anterior.
 *
 * DECISION (botón de guardar): UsuarioForm ya incluye su propio botón de
 * envío (type="submit", fullWidth) dentro del formulario. Este modal NO
 * añade un segundo botón de confirmar en DialogActions -evitaría dos
 * botones de guardar en pantalla-; DialogActions solo aporta "Cancelar".
 *
 * DECISION (TASK-012, conflicto 409 al crear): api/usuarios.api.js adjunta
 * `isBaja`/`userId` al error solo cuando el 409 corresponde a un usuario dado
 * de baja (el username nunca se libera: sigue referenciado como texto sin FK
 * desde grupos_n2.preparador, registro_contenedores.preparador y las ventas
 * de Eurowin, ver milestones.md decisión 4-5). Ese caso se resuelve con un
 * `Alert` inline (SeccionConflictoCreacion) con CTA "Restaurar usuario en su
 * lugar", nunca con un toast: el toast desaparece solo y se llevaría consigo
 * la única acción que resuelve el conflicto. El 409 "ya existe" (usuario
 * activo, sin isBaja) se distingue del anterior únicamente por ese flag y se
 * muestra con el mismo componente pero sin botón, porque restaurar una
 * cuenta activa no tiene sentido. El estado `conflicto` se limpia al
 * reintentar el envío y al cerrarse el modal (ver useEffect), para no dejar
 * un aviso de una cuenta distinta a la que se está creando ahora.
 */
const ModalUsuario = ({ show, onHide, usuario, crearUsuario, actualizarUsuario, restaurarUsuario }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { state, updateState } = useContext(StateContext);
    const { roles, loadingRoles, errorRoles } = useUsuarioFormData();
    const [loading, setLoading] = useState(false);
    const [conflicto, setConflicto] = useState(null); // { tipo: "baja"|"existente", mensaje, userId? }

    const esEdicion = Boolean(usuario?.id);

    // Al cerrarse el modal (éxito, cancelar o click fuera) no debe sobrevivir
    // el aviso de conflicto de la sesión anterior: si se reabre para dar de
    // alta a otra persona, no debe arrastrar el 409 de la vez anterior.
    useEffect(() => {
        if (!show) {
            setConflicto(null);
        }
    }, [show]);

    const mostrarToastError = useCallback(
        (mensaje) => updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, "error")] }),
        [state.toasts, updateState]
    );

    const mostrarToastExito = useCallback(
        (mensaje) => updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, "success")] }),
        [state.toasts, updateState]
    );

    const handleSubmit = useCallback(
        async (datos) => {
            setConflicto(null);
            setLoading(true);
            try {
                if (esEdicion) {
                    await actualizarUsuario(usuario.id, datos);
                    mostrarToastExito("Usuario actualizado correctamente");
                } else {
                    await crearUsuario(datos);
                    mostrarToastExito("Usuario creado correctamente");
                }
                onHide();
            } catch (err) {
                const mensajeBackend = err.response?.data?.message;

                if (err.isBaja) {
                    setConflicto({ tipo: "baja", mensaje: mensajeBackend || MENSAJE_CONFLICTO_BAJA, userId: err.userId });
                } else if (err.response?.status === 409) {
                    setConflicto({ tipo: "existente", mensaje: mensajeBackend || MENSAJE_CONFLICTO_EXISTENTE });
                } else {
                    mostrarToastError(mensajeBackend || err.message || (esEdicion ? MENSAJE_ERROR_EDITAR : MENSAJE_ERROR_CREAR));
                }
            } finally {
                setLoading(false);
            }
        },
        [esEdicion, usuario, crearUsuario, actualizarUsuario, onHide, mostrarToastExito, mostrarToastError]
    );

    // Restaura desde el propio modal de creación (TASK-012), sin cerrar y
    // abrir un modal distinto. Si falla, el conflicto NO se limpia: el Alert y
    // el CTA siguen visibles para reintentar, y los datos ya tecleados en
    // UsuarioForm se conservan (no se desmonta en ningún momento de este flujo).
    const handleRestaurarDesdeConflicto = useCallback(async () => {
        if (!conflicto?.userId) return;

        setLoading(true);
        try {
            const usuarioRestaurado = await restaurarUsuario(conflicto.userId);
            mostrarToastExito(
                `Cuenta de "${usuarioRestaurado?.username ?? conflicto.userId}" reactivada con su historial ` +
                    "intacto: no se creó un usuario nuevo"
            );
            setConflicto(null);
            onHide();
        } catch (err) {
            mostrarToastError(err.response?.data?.message || err.message || MENSAJE_ERROR_RESTAURAR);
        } finally {
            setLoading(false);
        }
    }, [conflicto, restaurarUsuario, onHide, mostrarToastExito, mostrarToastError]);

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2,
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: BADGE_COLORS.BLUE.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        {esEdicion ? (
                            <EditIcon sx={{ color: BADGE_COLORS.BLUE.text, fontSize: { xs: 20, sm: 24 } }} />
                        ) : (
                            <PersonAddIcon sx={{ color: BADGE_COLORS.BLUE.text, fontSize: { xs: 20, sm: 24 } }} />
                        )}
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
                    >
                        {esEdicion ? "Editar usuario" : "Nuevo usuario"}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onHide}
                    disabled={loading}
                    size="small"
                    sx={{ color: "#6c757d", "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" } }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial",
                }}
            >
                {show && (
                    <>
                        <SeccionConflictoCreacion
                            conflicto={conflicto}
                            loading={loading}
                            onRestaurar={handleRestaurarDesdeConflicto}
                        />
                        <UsuarioForm
                            key={usuario?.id ?? "nuevo"}
                            usuario={usuario}
                            roles={roles}
                            loadingRoles={loadingRoles}
                            errorRoles={errorRoles}
                            loading={loading}
                            onSubmit={handleSubmit}
                        />
                    </>
                )}
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" },
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        "&:hover": { borderColor: "#495057", backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                >
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalUsuario;
